import { reactive, ref } from 'vue';
import StudyLinkService from '@/services/studyLinkService';
import { defineStore } from 'pinia';
import { useStudyStore } from './study';
import { useFormStore } from './forms';
import { StudyLink } from '@/models/studyLink';
import { LoadingStateModel } from '@/models/loadingState';


export const useStudyLinkStore = defineStore('studyLink', () => {
  const studyStore = useStudyStore();
  const studyLink = ref<StudyLink>();
  const formStore = useFormStore();
  const studyLinkActivated = ref<boolean>();
  const updatedStudyLink = ref<StudyLink>();
  const viewEntriesPermissions = ['View all entries', 'View own entries'];
  const selectedViewEntryPermission = ref<string>(viewEntriesPermissions[0]);

  const isGettingStudyLink = reactive(new LoadingStateModel({ isLoading: false }));
  const getStudyLinkDetails = async () => {
    if (studyStore.selectedStudyId) {
      try {
        isGettingStudyLink.startLoading();
        studyLink.value = await StudyLinkService.getStudyLinkDetails(studyStore.selectedStudyId);
        if (studyLink.value) {
          if (studyLink.value.permissions.forms === true) {
            studyLink.value.permissions.forms = formStore.formList.map(form => form.base64Id) as string[];
          }
          if (studyLink.value.permissions.editForms === true) {
            studyLink.value.permissions.editForms = formStore.formList.map(form => form.base64Id) as string[];
          }
          studyLinkActivated.value = studyLink.value !== undefined;
          updatedStudyLink.value = JSON.parse(JSON.stringify(studyLink.value));
          selectedViewEntryPermission.value = updatedStudyLink.value!.permissions.viewEntries ?
            viewEntriesPermissions[0] :
            viewEntriesPermissions[1];
        } else {
          studyLinkActivated.value = studyLink.value !== undefined;
        }
      } catch (error) {
        isGettingStudyLink.setError(error);
      } finally {
        isGettingStudyLink.stopLoadingAndFinish();
      }
    }
  };


  const isActivatingStudyLink = reactive(new LoadingStateModel({ isLoading: false }));
  const activateStudyLink = async () => {
    if (studyStore.selectedStudyId) {
      try {
        isActivatingStudyLink.startLoading();
        await StudyLinkService.activateStudyLink(studyStore.selectedStudyId);
        isActivatingStudyLink.setSuccess(`Sharable study link activiated.`);
        await getStudyLinkDetails();
      } catch (error) {
        isActivatingStudyLink.setError(error);
      } finally {
        isActivatingStudyLink.stopLoadingAndFinish();
      }
    }
  };


  const isDeactivatingStudyLink = reactive(new LoadingStateModel({ isLoading: false }));
  const deactivateStudyLink = async () => {
    if (studyStore.selectedStudyId) {
      try {
        isDeactivatingStudyLink.startLoading();
        await StudyLinkService.deactivateStudyLink(studyStore.selectedStudyId);
        isDeactivatingStudyLink.setSuccess(`Sharable study link diactivated!`);
        await getStudyLinkDetails();
      } catch (error) {
        isDeactivatingStudyLink.setError(error);
      } finally {
        isDeactivatingStudyLink.stopLoadingAndFinish();
      }
    }
  };


  const isResettingStudyLink = reactive(new LoadingStateModel({ isLoading: false }));
  const resetStudyLink = async () => {
    if (studyStore.selectedStudyId) {
      try {
        isResettingStudyLink.startLoading();
        await StudyLinkService.resetStudyLink(studyStore.selectedStudyId);
        isResettingStudyLink.setSuccess(`Sharable study link did reset.`);
        await getStudyLinkDetails();
      } catch (error) {
        isResettingStudyLink.setError(error);
      } finally {
        isResettingStudyLink.stopLoadingAndFinish();
      }
    }
  };


  const isUpdatingStudyLink = reactive(new LoadingStateModel({ isLoading: false }));
  const updateStudyLinkPermissions = async (linkUpdates: StudyLink) => {
    if (studyStore.selectedStudyId) {
      try {
        isUpdatingStudyLink.startLoading();
        await StudyLinkService.updateStudyLink(studyStore.selectedStudyId, linkUpdates);
        isUpdatingStudyLink.setSuccess(`Sharable study link activiated.`);
        await getStudyLinkDetails();
      } catch (error) {
        isUpdatingStudyLink.setError(error);
      } finally {
        isUpdatingStudyLink.stopLoadingAndFinish();
      }
    }
  };


  return {
    getStudyLinkDetails,
    activateStudyLink,
    deactivateStudyLink,
    resetStudyLink,
    updateStudyLinkPermissions,
    studyLink,
    studyLinkActivated,
    updatedStudyLink,
    selectedViewEntryPermission,
    viewEntriesPermissions,
    isGettingStudyLink,
    isActivatingStudyLink,
    isDeactivatingStudyLink,
    isResettingStudyLink,
    isUpdatingStudyLink
  };
});
